export const PlanCycle = {
  MONTHLY: "MONTHLY",
  YEARLY: "YEARLY",
} as const;

export type PlanCycleValue = (typeof PlanCycle)[keyof typeof PlanCycle];

export const HOMOLOGATION_MONTHS = 12;
export const DEFAULT_MAX_PIX_INSTALLMENTS = 12;
export const MIN_PIX_INSTALLMENTS = 2;
export const CONSTELLATION_PRICE_MIN = 80;
export const CONSTELLATION_PRICE_MAX = 800;

export type PlanCommerceFields = {
  isRecurring?: boolean;
  cycle?: string | null;
  allowPixInstallments?: boolean;
  maxPixInstallments?: number;
  cancellable?: boolean;
  homologationMonths?: number;
  homologationOnly?: boolean;
  value?: number;
  userType?: string;
  name?: string;
  sessionLabel?: string;
};

export function roundMoney(value: number): number {
  return Math.round(Number(value || 0) * 100) / 100;
}

export function normalizePlanCycle(cycle?: string | null): PlanCycleValue {
  return cycle === PlanCycle.YEARLY ? PlanCycle.YEARLY : PlanCycle.MONTHLY;
}

export function homologationCoveredMonths(plan: PlanCommerceFields): number {
  if (normalizePlanCycle(plan.cycle) === PlanCycle.YEARLY) return HOMOLOGATION_MONTHS;
  const months = Number(plan.homologationMonths || 0);
  return months >= HOMOLOGATION_MONTHS ? HOMOLOGATION_MONTHS : 0;
}

export function planRequiresHomologation(plan: PlanCommerceFields): boolean {
  return homologationCoveredMonths(plan) >= HOMOLOGATION_MONTHS || plan.cancellable === false;
}

export function planIsCancellable(plan: PlanCommerceFields): boolean {
  if (plan.cancellable === false) return false;
  return !planRequiresHomologation(plan);
}

export function planAllowsPixInstallments(plan: PlanCommerceFields): boolean {
  return !!plan.allowPixInstallments || planRequiresHomologation(plan);
}

export function planPixAvailable(plan: PlanCommerceFields): boolean {
  return !plan.isRecurring || planAllowsPixInstallments(plan);
}

export function maxPixInstallmentsForPlan(plan: PlanCommerceFields): number {
  const max = Number(plan.maxPixInstallments || DEFAULT_MAX_PIX_INSTALLMENTS);
  if (!Number.isFinite(max)) return DEFAULT_MAX_PIX_INSTALLMENTS;
  return Math.min(DEFAULT_MAX_PIX_INSTALLMENTS, Math.max(1, Math.floor(max)));
}

export function pixInstallmentOptions(plan: PlanCommerceFields): number[] {
  if (!planAllowsPixInstallments(plan)) return [1];
  const max = maxPixInstallmentsForPlan(plan);
  const options = [1, 3, 6, 12].filter((n) => n === 1 || n <= max);
  if (max > 1 && !options.includes(max)) options.push(max);
  return options.sort((a, b) => a - b);
}

/** Valor total do compromisso (ano homologado ou preço do plano). */
export function planCommitmentTotal(plan: PlanCommerceFields): number {
  const value = roundMoney(Number(plan.value || 0));
  if (normalizePlanCycle(plan.cycle) === PlanCycle.YEARLY) return value;
  const months = homologationCoveredMonths(plan);
  if (months > 1) return roundMoney(value * months);
  return value;
}

export function resolvePixInstallmentValues(
  plan: PlanCommerceFields,
  installmentCount: number,
): { totalValue: number; parcelValue: number; installmentCount: number } {
  const totalValue = planCommitmentTotal(plan);
  const count = Math.max(1, Math.floor(Number(installmentCount) || 1));
  return {
    totalValue,
    parcelValue: roundMoney(totalValue / count),
    installmentCount: count,
  };
}

export function formatConstellationPrice(value?: number | null): string | null {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function isPatientConstellationPlan(plan: Pick<PlanCommerceFields, "userType" | "name" | "sessionLabel">): boolean {
  if ((plan.userType || "").toLowerCase() !== "patient") return false;
  const blob = `${plan.name || ""} ${plan.sessionLabel || ""}`.toLowerCase();
  if (/particip|represent|clube/.test(blob)) return false;
  const label = (plan.sessionLabel || "").toLowerCase();
  return /constel/.test(blob) || label.includes("constel");
}
